import { useCallback, useEffect, useRef } from 'react';

/**
 * Manages the open/close lifecycle for a native `<dialog>` element.
 *
 * Returns a ref to attach to the `<dialog>` and two stable callbacks —
 * `openDialog` and `closeDialog` — that call the browser's native
 * `showModal()` / `close()` APIs. Consumers never manipulate `open`
 * directly; all state is owned by the DOM element.
 *
 * Consumed by booking-detail and admin confirm surfaces; share the single
 * implementation rather than re-creating it per dialog.
 */
export const useDialogElement = (): {
  dialogRef: React.RefObject<HTMLDialogElement | null>;
  openDialog: () => void;
  closeDialog: () => void;
} => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  const openDialog = useCallback((): void => {
    dialogRef.current?.showModal();
  }, []);

  const closeDialog = useCallback((): void => {
    dialogRef.current?.close();
  }, []);

  // Close the dialog when the user presses Escape (the browser fires the
  // `cancel` event on the dialog element, which we forward to `close`).
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const handleCancel = (e: Event): void => {
      e.preventDefault();
      el.close();
    };
    el.addEventListener('cancel', handleCancel);
    return () => {
      el.removeEventListener('cancel', handleCancel);
    };
  }, []);

  return { dialogRef, openDialog, closeDialog };
};
